import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: string;
  schema?: Record<string, unknown>[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath = '/',
  ogType = 'website',
  schema = [],
}: SEOHeadProps) {
  const fullTitle = title.includes('禁漫天堂') ? title : `${title} - 禁漫天堂`;
  const canonicalUrl = `https://jmtthub.com${canonicalPath}`;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    const canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonicalEl) {
      canonicalEl.setAttribute('href', canonicalUrl);
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = canonicalUrl;
      document.head.appendChild(link);
    }

    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:locale', 'zh_CN', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);

    const scriptIds: string[] = [];
    schema.forEach((s) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(s);
      const id = `schema-${Math.random().toString(36).slice(2)}`;
      script.id = id;
      scriptIds.push(id);
      document.head.appendChild(script);
    });

    return () => {
      document.title = previousTitle;
      scriptIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [fullTitle, description, keywords, canonicalUrl, ogType, schema]);

  return null;
}